/**
 * ROUTES CONFIGURATION
 * Central route definitions for the application
 */

export const ROUTES = {
  // Public routes
  HOME: '/',
  LOGIN: '/login',
  SYSTEM_CONTROL: '/system-control',
  
  // Main application routes
  DASHBOARD: '/dashboard',
  MULTI_TIMEFRAME: '/multi-timeframe',
  KNOWLEDGE_GRAPH: '/knowledge-graph',
  DREAM_ENGINE: '/dream-engine',
  PERFORMANCE: '/performance',
  LEARNING_CONSOLE: '/learning-console',
  CHART: '/chart',
  SHAPES: '/shapes',
  POLICY_APPROVALS: '/policy-approvals',
  TRAINING_CENTER: '/training',
  
  // Admin routes
  ADMIN: '/admin',
  ADMIN_SERVICES: '/admin/services',
  ADMIN_USERS: '/admin/users',
  ADMIN_LOGS: '/admin/logs',
  ADMIN_METRICS: '/admin/metrics',
  ADMIN_ALERTS: '/admin/alerts',
  ADMIN_HEALTH: '/admin/health',
  ADMIN_CONFIG: '/admin/config',
  
  // Error routes
  NOT_FOUND: '*',
} as const;

export const ROUTE_LABELS = {
  [ROUTES.HOME]: 'Home',
  [ROUTES.DASHBOARD]: 'Dashboard',
  [ROUTES.SYSTEM_CONTROL]: 'System Control',
  [ROUTES.MULTI_TIMEFRAME]: 'Multi-Timeframe',
  [ROUTES.KNOWLEDGE_GRAPH]: 'Knowledge Graph',
  [ROUTES.DREAM_ENGINE]: 'Dream Engine',
  [ROUTES.PERFORMANCE]: 'Performance',
  [ROUTES.LEARNING_CONSOLE]: 'Learning Console',
  [ROUTES.CHART]: 'Pro Chart',
  [ROUTES.SHAPES]: 'Shapes Manager',
  [ROUTES.POLICY_APPROVALS]: 'Policy Approvals',
  [ROUTES.TRAINING_CENTER]: 'Training Center',
  [ROUTES.ADMIN]: 'Admin',
  [ROUTES.ADMIN_SERVICES]: 'Service Monitoring',
  [ROUTES.ADMIN_USERS]: 'User Management',
  [ROUTES.ADMIN_LOGS]: 'Logs Explorer',
  [ROUTES.ADMIN_METRICS]: 'Metrics Viewer',
  [ROUTES.ADMIN_ALERTS]: 'Alerts Manager',
  [ROUTES.ADMIN_HEALTH]: 'System Health',
  [ROUTES.ADMIN_CONFIG]: 'Configuration',
} as const;

export const ROUTE_PERMISSIONS = {
  [ROUTES.DASHBOARD]: ['view_dashboard'],
  [ROUTES.SYSTEM_CONTROL]: ['view_dashboard'],
  [ROUTES.MULTI_TIMEFRAME]: ['view_charts'],
  [ROUTES.KNOWLEDGE_GRAPH]: ['view_knowledge_graph'],
  [ROUTES.DREAM_ENGINE]: ['view_charts'],
  [ROUTES.PERFORMANCE]: ['view_performance'],
  [ROUTES.LEARNING_CONSOLE]: ['view_learning'],
  [ROUTES.CHART]: ['view_charts'],
  [ROUTES.SHAPES]: ['view_charts'],
  [ROUTES.POLICY_APPROVALS]: ['manage_config'],
  [ROUTES.TRAINING_CENTER]: ['view_learning'],
  [ROUTES.ADMIN_SERVICES]: ['manage_services'],
  [ROUTES.ADMIN_USERS]: ['manage_users'],
  [ROUTES.ADMIN_LOGS]: ['view_logs'],
  [ROUTES.ADMIN_METRICS]: ['view_metrics'],
  [ROUTES.ADMIN_ALERTS]: ['manage_alerts'],
  [ROUTES.ADMIN_HEALTH]: ['view_logs', 'view_metrics'],
  [ROUTES.ADMIN_CONFIG]: ['manage_config'],
} as const;

export const NAVIGATION_ITEMS = [
  {
    path: ROUTES.SYSTEM_CONTROL,
    label: ROUTE_LABELS[ROUTES.SYSTEM_CONTROL],
    icon: '🛰️',
    permissions: ROUTE_PERMISSIONS[ROUTES.SYSTEM_CONTROL],
  },
  {
    path: ROUTES.DASHBOARD,
    label: ROUTE_LABELS[ROUTES.DASHBOARD],
    icon: '🎯',
    permissions: ROUTE_PERMISSIONS[ROUTES.DASHBOARD],
  },
  {
    path: ROUTES.MULTI_TIMEFRAME,
    label: ROUTE_LABELS[ROUTES.MULTI_TIMEFRAME],
    icon: '📊',
    permissions: ROUTE_PERMISSIONS[ROUTES.MULTI_TIMEFRAME],
  },
  {
    path: ROUTES.KNOWLEDGE_GRAPH,
    label: ROUTE_LABELS[ROUTES.KNOWLEDGE_GRAPH],
    icon: '🧠',
    permissions: ROUTE_PERMISSIONS[ROUTES.KNOWLEDGE_GRAPH],
  },
  {
    path: ROUTES.DREAM_ENGINE,
    label: ROUTE_LABELS[ROUTES.DREAM_ENGINE],
    icon: '🔮',
    permissions: ROUTE_PERMISSIONS[ROUTES.DREAM_ENGINE],
  },
  {
    path: ROUTES.CHART,
    label: ROUTE_LABELS[ROUTES.CHART],
    icon: '📉',
    permissions: ROUTE_PERMISSIONS[ROUTES.CHART],
  },
  {
    path: ROUTES.SHAPES,
    label: ROUTE_LABELS[ROUTES.SHAPES],
    icon: '▭',
    permissions: ROUTE_PERMISSIONS[ROUTES.SHAPES],
  },
  {
    path: ROUTES.PERFORMANCE,
    label: ROUTE_LABELS[ROUTES.PERFORMANCE],
    icon: '📈',
    permissions: ROUTE_PERMISSIONS[ROUTES.PERFORMANCE],
  },
  {
    path: ROUTES.LEARNING_CONSOLE,
    label: ROUTE_LABELS[ROUTES.LEARNING_CONSOLE],
    icon: '🎓',
    permissions: ROUTE_PERMISSIONS[ROUTES.LEARNING_CONSOLE],
  },
  {
    path: ROUTES.POLICY_APPROVALS,
    label: ROUTE_LABELS[ROUTES.POLICY_APPROVALS],
    icon: '🛡️',
    permissions: ROUTE_PERMISSIONS[ROUTES.POLICY_APPROVALS],
  },
  {
    path: ROUTES.TRAINING_CENTER,
    label: ROUTE_LABELS[ROUTES.TRAINING_CENTER],
    icon: '🏋️',
    permissions: ROUTE_PERMISSIONS[ROUTES.TRAINING_CENTER],
  },
] as const;

export const ADMIN_NAVIGATION_ITEMS = [
  {
    path: ROUTES.ADMIN_SERVICES,
    label: ROUTE_LABELS[ROUTES.ADMIN_SERVICES],
    icon: '⚙️',
    permissions: ROUTE_PERMISSIONS[ROUTES.ADMIN_SERVICES],
  },
  {
    path: ROUTES.ADMIN_HEALTH,
    label: ROUTE_LABELS[ROUTES.ADMIN_HEALTH],
    icon: '💚',
    permissions: ROUTE_PERMISSIONS[ROUTES.ADMIN_HEALTH],
  },
  {
    path: ROUTES.ADMIN_LOGS,
    label: ROUTE_LABELS[ROUTES.ADMIN_LOGS],
    icon: '📝',
    permissions: ROUTE_PERMISSIONS[ROUTES.ADMIN_LOGS],
  },
  {
    path: ROUTES.ADMIN_METRICS,
    label: ROUTE_LABELS[ROUTES.ADMIN_METRICS],
    icon: '📊',
    permissions: ROUTE_PERMISSIONS[ROUTES.ADMIN_METRICS],
  },
  {
    path: ROUTES.ADMIN_ALERTS,
    label: ROUTE_LABELS[ROUTES.ADMIN_ALERTS],
    icon: '🔔',
    permissions: ROUTE_PERMISSIONS[ROUTES.ADMIN_ALERTS],
  },
  {
    path: ROUTES.ADMIN_USERS,
    label: ROUTE_LABELS[ROUTES.ADMIN_USERS],
    icon: '👥',
    permissions: ROUTE_PERMISSIONS[ROUTES.ADMIN_USERS],
  },
  {
    path: ROUTES.ADMIN_CONFIG,
    label: ROUTE_LABELS[ROUTES.ADMIN_CONFIG],
    icon: '🔧',
    permissions: ROUTE_PERMISSIONS[ROUTES.ADMIN_CONFIG],
  },
] as const;
